import React from 'react';
import { FaWindows, FaDownload, FaFolderOpen, FaMousePointer, FaFileArchive } from 'react-icons/fa';
import { motion } from 'framer-motion';

const DesktopApp = () => {
  const steps = [
    {
      icon: <FaDownload className="text-pink-500 text-xl" />,
      title: "Download the ZIP",
      text: "Click the download button to get the application package"
    },
    {
      icon: <FaFileArchive className="text-purple-500 text-xl" />,
      title: "Extract Files",
      text: "Right-click the ZIP and select 'Extract All'"
    },
    {
      icon: <FaFolderOpen className="text-blue-400 text-xl" />,
      title: "Open Folder",
      text: "Navigate to the extracted contents"
    },
    {
      icon: <FaMousePointer className="text-green-400 text-xl" />,
      title: "Run Application",
      text: "Double-click QCBT-DESKTOP to launch"
    }
  ];

  return (
    <div className="relative py-20 overflow-hidden bg-gray-900 hidden ">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-purple-600 rounded-full filter blur-3xl opacity-40 mix-blend-multiply"></div>
        <div className="absolute top-0 right-1/4 w-72 h-72 bg-pink-600 rounded-full filter blur-3xl opacity-40 mix-blend-multiply"></div>
        <div className="absolute bottom-0 left-1/3 w-72 h-72 bg-blue-600 rounded-full filter blur-3xl opacity-40 mix-blend-multiply"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl font-bold text-white mb-3">
            Elevate Your Experience
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            Our{" "}
            <span className="font-semibold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              desktop application
            </span>{" "}
            brings powerful features to your fingertips
          </p>
          <div className="mt-6 flex justify-center">
            <div className="w-24 h-1.5 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" />
          </div>
        </motion.div>

        {/* Content - Improved layout */}
        <div className="flex flex-col lg:flex-row items-stretch gap-8 xl:gap-12">
          {/* App Preview - Now takes half width */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full lg:w-1/2 flex flex-col"
          >
            <div className="flex-1 flex items-center justify-center p-4  ">
              <img
                src="/desktop_app.png"
                className="w-full h-auto object-contain"
                alt="Desktop App Preview"
              />
            </div>

            {/* Download button - Centered below image */}
            <motion.div className=" text-center">
              <a
                href="/QCBT-DESKTOP-win32-x64.zip"
                className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium rounded-lg px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <FaWindows className="w-5 h-5 mr-2" />
                <span className="text-base">Download for Windows</span>
              </a>
              <p className="text-gray-400 text-xs mt-2">
                Windows 10/11 • 64-bit • Portable
              </p>
            </motion.div>
          </motion.div>

          {/* Steps - Now takes half width and matches height */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-full lg:w-1/2"
          >
            <div className="h-full bg-gray-800 bg-opacity-60 backdrop-blur-md rounded-xl p-6 border border-gray-700 shadow-xl flex flex-col">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-5 pb-3 border-b border-gray-700">
                Simple Setup Process
              </h3>

              <div className="space-y-5 flex-1">
                {steps.map((step, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ x: 5 }}
                    className="flex items-start group"
                  >
                    <div className="flex-shrink-0 mt-1 mr-4 p-2 bg-gray-700 bg-opacity-50 rounded-lg group-hover:bg-opacity-80 transition-all">
                      {step.icon}
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-white group-hover:text-pink-300 transition-colors">
                        {step.title}
                      </h4>
                      <p className="text-gray-300 text-sm mt-1">{step.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-gray-700">
                <div className="flex items-center">
                  <span className="flex w-8 h-8 bg-green-500 bg-opacity-20 rounded-full items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </span>
                  <p className="text-sm text-gray-300">
                    <span className="font-medium text-green-400">
                      No installation needed
                    </span>{" "}
                    - just extract and run!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional info */}
        <div className="mt-12 md:mt-16 text-center">
          <p className="text-gray-400 text-sm">
            Need help?{" "}
            <a
              href="#"
              className="text-purple-400 hover:text-purple-300 underline transition-colors"
            >
              View installation guide
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DesktopApp;